import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowUpDown, ChevronDown, ChevronUp, Columns3, ExternalLink, FileText, Plus, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCompanyLookup, useMe, useOppPicklists, useOpportunities, useResourceLookup, useUpdateOpportunity, type OppFilters } from '@/api/hooks';
import type { Opportunity, OppPicklists, PicklistValue } from '@/api/client';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';
import { OpportunitiesHeaderStrip } from '@/components/OpportunitiesHeaderStrip';
import { OpportunityCard as OppCard } from '@/components/cards/OpportunityCard';
import { StatusBadge } from '@/components/StatusBadge';
import { StagePicker } from '@/components/StagePicker';
import { MultiSelect } from '@/components/MultiSelect';
import { StringMultiSelect } from '@/components/StringMultiSelect';
import { StatusPicker } from '@/components/StatusPicker';
import { PipelineAssistant } from '@/components/SalesAssistant';
import { fmtCurrency } from '@/lib/cobalt-helpers';
import { ROUTES, oppPath } from '@/lib/routes';

const AUTOTASK_OPP_URL = 'https://ww31.autotask.net/Mvc/CRM/OpportunityDetail.mvc?opportunityId=';

// ---- Column definitions ----

type ColumnKey = 'id' | 'title' | 'company' | 'serviceCategory' | 'amount' | 'probability' | 'stage' | 'status' | 'owner' | 'projectedCloseDate' | 'createDate' | 'lastActivity' | 'sow' | 'openInAutotask';
type SortKey = Exclude<ColumnKey, 'sow' | 'openInAutotask'>;
type SortDir = 'asc' | 'desc';
type SortState = { key: SortKey; dir: SortDir } | null;

interface ColumnDef {
  key: ColumnKey;
  label: string;
  defaultWidth: number;
  minWidth: number;
  required?: boolean;
  unsortable?: boolean;
}

const ALL_COLUMNS: ColumnDef[] = [
  { key: 'id',                label: '#',                defaultWidth: 70,  minWidth: 50,  required: true },
  { key: 'title',             label: 'Title',            defaultWidth: 280, minWidth: 120, required: true },
  { key: 'company',           label: 'Company',          defaultWidth: 180, minWidth: 80 },
  { key: 'serviceCategory',   label: 'Service Category', defaultWidth: 160, minWidth: 80 },
  { key: 'stage',             label: 'Stage',            defaultWidth: 195, minWidth: 80 },
  { key: 'status',            label: 'Status',           defaultWidth: 115, minWidth: 80 },
  { key: 'amount',            label: 'Amount',           defaultWidth: 120, minWidth: 80 },
  { key: 'probability',       label: 'Probability',      defaultWidth: 110, minWidth: 80 },
  { key: 'owner',             label: 'Owner',            defaultWidth: 160, minWidth: 80 },
  { key: 'projectedCloseDate',label: 'Close Date',       defaultWidth: 120, minWidth: 80 },
  { key: 'createDate',        label: 'Created',          defaultWidth: 110, minWidth: 80 },
  { key: 'lastActivity',      label: 'Last Activity',    defaultWidth: 120, minWidth: 80 },
  { key: 'sow',               label: 'SOW',              defaultWidth: 55,  minWidth: 55,  unsortable: true },
  { key: 'openInAutotask',    label: '',                 defaultWidth: 40,  minWidth: 40,  required: true, unsortable: true },
];

const COL_BY_KEY = new Map(ALL_COLUMNS.map((c) => [c.key, c]));

const DEFAULT_VISIBLE: ColumnKey[] = ['id', 'title', 'company', 'serviceCategory', 'stage', 'status', 'amount', 'probability', 'owner', 'projectedCloseDate', 'lastActivity', 'sow'];

// ---- Persistence helpers ----

function loadFilters(key: string, defaults: OppFilters): OppFilters {
  try { const r = localStorage.getItem(key); return r ? { ...defaults, ...JSON.parse(r) } : defaults; } catch { return defaults; }
}
function loadSort(key: string): SortState {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : null; } catch { return null; }
}
function loadWidths(key: string): Partial<Record<ColumnKey, number>> {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : {}; } catch { return {}; }
}
function loadVisible(key: string): Set<ColumnKey> {
  try {
    const r = localStorage.getItem(key);
    if (r) {
      const set = new Set(JSON.parse(r) as ColumnKey[]);
      // One-time: surface the Status column for users who already had saved column prefs.
      const migKey = `${key}:statusMigrated`;
      if (!localStorage.getItem(migKey)) {
        set.add('status');
        localStorage.setItem(migKey, '1');
      }
      return set;
    }
  } catch {}
  return new Set(DEFAULT_VISIBLE);
}

// ---- Main component ----

interface Props { assignedTo: 'me' | 'any' }

const STORAGE_PREFIX = 'cosmos:opps';

export default function OpportunitiesPage({ assignedTo }: Props) {
  const navigate = useNavigate();
  const storageKey = `${STORAGE_PREFIX}:${assignedTo}`;

  // Open Opps is open-only — the server defaults to Active (status 1). Closed/lost/etc.
  // live on the Search page, so no status filter is exposed here.
  const defaultFilters: OppFilters = useMemo(() => ({ assignedTo, stage: [], search: '' }), [assignedTo]);

  const [filters, setFilters]         = useState<OppFilters>(() => loadFilters(storageKey, defaultFilters));
  // Default sort: most recently active first (overridable; persists once the user sorts).
  const [sort, setSort]               = useState<SortState>(() => loadSort(`${storageKey}:sort`) ?? { key: 'lastActivity', dir: 'desc' });
  const [widths, setWidths]           = useState(() => loadWidths(`${storageKey}:widths`));
  const [visible, setVisible]         = useState(() => loadVisible(`${storageKey}:visible`));
  const [catFilter, setCatFilter]     = useState<string[]>(() => {
    try { const r = localStorage.getItem(`${storageKey}:catFilter`); return r ? JSON.parse(r) : []; } catch { return []; }
  });

  // Keep assignedTo in sync when the route changes (same component, different prop)
  useEffect(() => {
    setFilters((f) => ({ ...f, assignedTo }));
  }, [assignedTo]);

  useEffect(() => { localStorage.setItem(storageKey, JSON.stringify(filters)); }, [filters, storageKey]);
  useEffect(() => {
    if (sort) localStorage.setItem(`${storageKey}:sort`, JSON.stringify(sort));
    else localStorage.removeItem(`${storageKey}:sort`);
  }, [sort, storageKey]);
  useEffect(() => { localStorage.setItem(`${storageKey}:widths`, JSON.stringify(widths)); }, [widths, storageKey]);
  useEffect(() => { localStorage.setItem(`${storageKey}:visible`, JSON.stringify(Array.from(visible))); }, [visible, storageKey]);
  useEffect(() => { localStorage.setItem(`${storageKey}:catFilter`, JSON.stringify(catFilter)); }, [catFilter, storageKey]);

  // Text search is applied client-side over the loaded rows (see filteredItems below),
  // so it is not sent to the server — avoids a request per keystroke and lets us match
  // on company name (resolved client-side) as well as title / #.
  const opps        = useOpportunities({ ...filters, search: '' }, { scoped: true });
  const picklists   = useOppPicklists();
  const updateOpp   = useUpdateOpportunity();

  const items = opps.data?.items ?? [];

  // Resolve company / owner names for ALL loaded rows so client-side search can match on
  // company name and names are ready regardless of the active filters.
  const companyIds  = useMemo(() => [...new Set(items.map((o) => o.companyID).filter((v): v is number => v != null))], [items]);
  const ownerIds    = useMemo(() => [...new Set(items.map((o) => o.ownerResourceID).filter((v): v is number => v != null))], [items]);
  const companies   = useCompanyLookup(companyIds);
  const owners      = useResourceLookup(ownerIds);

  const companyMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of companies.data?.items ?? []) m.set(c.id, c.companyName);
    return m;
  }, [companies.data]);

  const resourceMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of owners.data?.items ?? []) m.set(r.id, `${r.firstName} ${r.lastName}`.trim());
    return m;
  }, [owners.data]);

  // Category filter (Service Category UDF) + client-side text search (title / # / company).
  // Status filtering is now done server-side via filters.status.
  const searchQuery = filters.search.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    return items.filter((o) => {
      if (catFilter.length > 0) {
        const cat = o.userDefinedFields?.find((f) => f.name === 'Service Category')?.value;
        if (cat == null || !catFilter.includes(cat)) return false;
      }
      if (searchQuery) {
        const company = o.companyID != null ? (companyMap.get(o.companyID) ?? '') : '';
        const matches =
          (o.title ?? '').toLowerCase().includes(searchQuery) ||
          String(o.id).includes(searchQuery) ||
          company.toLowerCase().includes(searchQuery);
        if (!matches) return false;
      }
      return true;
    });
  }, [items, catFilter, searchQuery, companyMap]);

  const stageMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of picklists.data?.stage ?? []) m.set(s.value, s.label);
    return m;
  }, [picklists.data]);

  const statusMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of picklists.data?.status ?? []) m.set(s.value, s.label);
    return m;
  }, [picklists.data]);

  const { data: me } = useMe();
  const myResourceId = me?.item?.id;

  // ---- Sort ----

  const sortedItems = useMemo(() => {
    if (!sort) return filteredItems;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const get = (o: Opportunity): string | number => {
      switch (sort.key) {
        case 'id':                return o.id;
        case 'title':             return (o.title ?? '').toLowerCase();
        case 'company':           return (o.companyID != null ? companyMap.get(o.companyID) ?? '' : '').toLowerCase();
        case 'serviceCategory':   return (o.userDefinedFields?.find((f) => f.name === 'Service Category')?.value ?? '').toLowerCase();
        case 'stage':             return (o.stage != null ? stageMap.get(String(o.stage)) ?? '' : '').toLowerCase();
        case 'status':            return (o.status != null ? statusMap.get(String(o.status)) ?? '' : '').toLowerCase();
        case 'amount':            return o.amount ?? (dir === 1 ? -1 : Number.MAX_SAFE_INTEGER);
        case 'probability':       return o.probability ?? (dir === 1 ? -1 : Number.MAX_SAFE_INTEGER);
        case 'owner':             return (o.ownerResourceID != null ? resourceMap.get(o.ownerResourceID) ?? '' : '').toLowerCase();
        case 'projectedCloseDate':return o.projectedCloseDate ? new Date(o.projectedCloseDate).getTime() : (dir === 1 ? Number.MAX_SAFE_INTEGER : -1);
        case 'createDate':        return o.createDate ? new Date(o.createDate).getTime() : 0;
        case 'lastActivity':      return o.lastActivity ? new Date(o.lastActivity).getTime() : (dir === 1 ? Number.MAX_SAFE_INTEGER : -1);
      }
    };
    return [...filteredItems].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [filteredItems, sort, companyMap, stageMap, statusMap, resourceMap]);

  // ---- Visible columns ----

  const visibleCols = useMemo(() => ALL_COLUMNS.filter((c) => c.required || visible.has(c.key)), [visible]);

  // ---- Column resize ----

  const dragRef = useRef<{ key: ColumnKey; startX: number; startWidth: number } | null>(null);
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const min = COL_BY_KEY.get(drag.key)?.minWidth ?? 60;
      setWidths((w) => ({ ...w, [drag.key]: Math.max(min, drag.startWidth + (e.clientX - drag.startX)) }));
    };
    const onUp = () => { if (dragRef.current) document.body.style.cursor = ''; dragRef.current = null; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
  }, []);

  const startResize = useCallback((key: ColumnKey, e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragRef.current = { key, startX: e.clientX, startWidth: widths[key] ?? COL_BY_KEY.get(key)?.defaultWidth ?? 120 };
    document.body.style.cursor = 'col-resize';
  }, [widths]);

  function cycleSort(key: SortKey) {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  }

  return (
    <div className="mx-auto w-full max-w-[1900px] px-4 py-4">
      <PageHeader
        title={assignedTo === 'me' ? 'My Opportunities' : 'Opportunities'}
        count={opps.data ? filteredItems.length : null}
        isFetching={opps.isFetching}
        subtitle="Open pipeline · Updated continuously"
        actions={
          <div className="flex flex-col items-end gap-1 md:flex-row md:items-center md:gap-2">
            <OppColumnPicker
              visible={visible}
              onToggle={(k, on) => setVisible((prev) => { const next = new Set(prev); on ? next.add(k) : next.delete(k); return next; })}
              onReset={() => setVisible(new Set(DEFAULT_VISIBLE))}
              onResetWidths={() => setWidths({})}
            />
            <Button size="sm" onClick={() => navigate(ROUTES.psa.opportunities.new)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />New Opportunity
            </Button>
          </div>
        }
      />

      <div className="my-4">
        <OpportunitiesHeaderStrip
          items={filteredItems}
          stageOpts={picklists.data?.stage ?? []}
          assignedTo={assignedTo}
          myResourceId={myResourceId}
        />
      </div>

      <div className="sticky top-0 z-10 -mx-4 mt-2 border-b bg-background/80 px-4 py-3 backdrop-blur">
        <OppFilterBar
          filters={filters}
          defaults={defaultFilters}
          stageOpts={picklists.data?.stage ?? []}
          serviceCategoryOpts={picklists.data?.serviceCategory ?? []}
          catFilter={catFilter}
          onCatFilterChange={setCatFilter}
          onChange={setFilters}
        />
      </div>

      {opps.error ? (
        <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          Failed to load opportunities: {opps.error instanceof Error ? opps.error.message : 'Unknown error'}
        </div>
      ) : null}

      {/* Mobile card list */}
      <div className="mt-4 md:hidden space-y-2">
        {filteredItems.length === 0 && !opps.isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No opportunities match these filters.</div>
        ) : null}
        {sortedItems.map((o) => (
          <OppCard
            key={o.id}
            o={o}
            companyMap={companyMap}
            stageMap={stageMap}
            stageOpts={picklists.data?.stage ?? []}
            statusOpts={picklists.data?.status ?? []}
            resourceMap={resourceMap}
            myResourceId={myResourceId}
            onStageChange={(id, stageId) => updateOpp.mutate({ id, fields: { stage: stageId } })}
            onStatusChange={(id, status) => updateOpp.mutate({ id, fields: { status } })}
            pendingStage={updateOpp.isPending && updateOpp.variables?.fields?.stage !== undefined && updateOpp.variables?.id === o.id}
            pendingStatus={updateOpp.isPending && updateOpp.variables?.fields?.status !== undefined && updateOpp.variables?.id === o.id}
          />
        ))}
      </div>

      {/* Desktop table */}
      <div className="mt-4 hidden md:block overflow-x-auto rounded-md border">
        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            {visibleCols.map((c) => <col key={c.key} style={{ width: widths[c.key] ?? c.defaultWidth }} />)}
          </colgroup>
          <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {visibleCols.map((c) => (
                <OppHeaderCell key={c.key} col={c} sort={sort} onSort={cycleSort} onResizeStart={startResize} />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {sortedItems.length === 0 && !opps.isLoading ? (
              <tr>
                <td colSpan={visibleCols.length} className="px-3 py-12 text-center text-muted-foreground">
                  No opportunities match these filters.
                </td>
              </tr>
            ) : null}
            {sortedItems.map((o) => (
              <tr key={o.id} className="hover:bg-muted/30">
                {visibleCols.map((c) => (
                  <td key={c.key} className="px-3 py-2 align-top overflow-hidden">
                    {renderCell(c.key, o, {
                      companyMap, stageMap,
                      stageOpts: picklists.data?.stage ?? [],
                      statusOpts: picklists.data?.status ?? [],
                      resourceMap, myResourceId,
                      onStageChange: (id, stageId) => updateOpp.mutate({ id, fields: { stage: stageId } }),
                      onStatusChange: (id, status) => updateOpp.mutate({ id, fields: { status } }),
                      pendingStage: updateOpp.isPending && updateOpp.variables?.fields?.stage !== undefined ? (updateOpp.variables?.id ?? null) : null,
                      pendingStatus: updateOpp.isPending && updateOpp.variables?.fields?.status !== undefined ? (updateOpp.variables?.id ?? null) : null,
                    })}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Floating sales assistant — pipeline scope follows the page (My Opps vs all) */}
      <PipelineAssistant scope={assignedTo} />
    </div>
  );
}

// OppCard is imported from @/components/cards/OpportunityCard

// ---- Cell rendering ----

interface CellCtx {
  companyMap: Map<number, string>;
  stageMap: Map<string, string>;
  stageOpts: PicklistValue[];
  statusOpts: PicklistValue[];
  resourceMap: Map<number, string>;
  myResourceId: number | undefined;
  onStageChange: (id: number, stageId: number) => void;
  onStatusChange: (id: number, status: number) => void;
  pendingStage: number | null;
  pendingStatus: number | null;
}

function renderCell(key: ColumnKey, o: Opportunity, ctx: CellCtx): ReactNode {
  switch (key) {
    case 'id':
      return <span className="font-mono text-xs text-muted-foreground">#{o.id}</span>;
    case 'title':
      return <Link to={oppPath(o.id)} className="block truncate font-medium hover:underline" title={o.title ?? ''}>{o.title}</Link>;
    case 'company': {
      const name = o.companyID != null ? ctx.companyMap.get(o.companyID) ?? `#${o.companyID}` : '—';
      return <div className="truncate text-muted-foreground" title={name}>{name}</div>;
    }
    case 'serviceCategory': {
      const cat = o.userDefinedFields?.find((f) => f.name === 'Service Category')?.value;
      return cat
        ? <span className="truncate text-xs text-foreground/80">{cat}</span>
        : <span className="text-muted-foreground">—</span>;
    }
    case 'stage':
      return (
        <StagePicker
          stageId={o.stage}
          stageOpts={ctx.stageOpts}
          onSelect={(stageId) => ctx.onStageChange(o.id, stageId)}
          pending={ctx.pendingStage === o.id}
          size="sm"
        />
      );
    case 'status':
      return (
        <StatusPicker
          statusId={o.status ?? null}
          statusOpts={ctx.statusOpts}
          onSelect={(status) => ctx.onStatusChange(o.id, status)}
          pending={ctx.pendingStatus === o.id}
          size="sm"
        />
      );
    case 'amount':
      return <div className="truncate tabular-nums">{o.amount != null ? fmtCurrency(o.amount) : '—'}</div>;
    case 'probability':
      return o.probability != null ? (
        <StatusBadge kind={probKind(o.probability)}>{o.probability}%</StatusBadge>
      ) : <span className="text-muted-foreground">—</span>;
    case 'owner': {
      const name = o.ownerResourceID != null ? ctx.resourceMap.get(o.ownerResourceID) ?? `#${o.ownerResourceID}` : '—';
      const isMe = ctx.myResourceId != null && o.ownerResourceID === ctx.myResourceId;
      return (
        <div className="truncate" title={name}>
          <span className={isMe ? 'font-medium' : 'text-muted-foreground'}>{name}</span>
          {isMe && <span className="ml-1 text-xs opacity-50">(you)</span>}
        </div>
      );
    }
    case 'projectedCloseDate':
      return <div className="truncate text-xs text-muted-foreground">{fmtDate(o.projectedCloseDate)}</div>;
    case 'createDate':
      return <div className="truncate text-xs text-muted-foreground">{fmtDate(o.createDate)}</div>;
    case 'lastActivity':
      return <div className="truncate text-xs text-muted-foreground">{fmtDate(o.lastActivity)}</div>;
    case 'sow': {
      const sowValue = o.userDefinedFields?.find((f) => f.name === 'Statement of Work')?.value;
      if (!sowValue?.trim()) return null;
      const isUrl = /^https?:\/\//i.test(sowValue.trim());
      const icon = <FileText className="h-3.5 w-3.5" />;
      return isUrl ? (
        <a
          href={sowValue.trim()}
          target="_blank"
          rel="noreferrer"
          title="Open Statement of Work"
          className="inline-flex h-6 w-6 items-center justify-center rounded text-brand-strong hover:bg-accent dark:text-brand"
          onClick={(e) => e.stopPropagation()}
        >
          {icon}
        </a>
      ) : (
        <span title={sowValue} className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground">
          {icon}
        </span>
      );
    }
    case 'openInAutotask':
      return (
        <a
          href={`${AUTOTASK_OPP_URL}${o.id}`}
          target="_blank"
          rel="noreferrer"
          title="Open in Autotask"
          className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      );
  }
}

// ---- Sub-components ----

function OppHeaderCell({
  col, sort, onSort, onResizeStart,
}: {
  col: ColumnDef;
  sort: SortState;
  onSort: (k: SortKey) => void;
  onResizeStart: (k: ColumnKey, e: React.PointerEvent) => void;
}) {
  const active = sort?.key === col.key;
  const Icon = !active ? ArrowUpDown : sort!.dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th className="relative px-3 py-2 text-left font-medium select-none">
      {col.unsortable ? (
        <span className="truncate">{col.label}</span>
      ) : (
        <button
          type="button"
          onClick={() => onSort(col.key as SortKey)}
          className={cn('inline-flex max-w-full items-center gap-1 truncate transition-colors', active ? 'text-foreground' : 'hover:text-foreground')}
        >
          <span className="truncate">{col.label}</span>
          <Icon className={cn('h-3 w-3 shrink-0', active ? 'opacity-80' : 'opacity-40')} />
        </button>
      )}
      <div
        onPointerDown={(e) => onResizeStart(col.key, e)}
        className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize touch-none hover:bg-primary/40 active:bg-primary/60"
      />
    </th>
  );
}

function OppColumnPicker({
  visible, onToggle, onReset, onResetWidths,
}: {
  visible: Set<ColumnKey>;
  onToggle: (k: ColumnKey, on: boolean) => void;
  onReset: () => void;
  onResetWidths: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="hidden h-7 gap-1.5 text-xs md:inline-flex">
          <Columns3 className="h-3.5 w-3.5" /> Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Show columns</DropdownMenuLabel>
        {ALL_COLUMNS.filter((c) => c.label !== '').map((c) => (
          <DropdownMenuCheckboxItem
            key={c.key}
            checked={c.required || visible.has(c.key)}
            disabled={c.required}
            onCheckedChange={(v) => onToggle(c.key, !!v)}
            onSelect={(e) => e.preventDefault()}
          >
            {c.label}{c.required && <span className="ml-2 text-xs text-muted-foreground">(required)</span>}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onReset}>Reset visibility</DropdownMenuItem>
        <DropdownMenuItem onSelect={onResetWidths}>Reset column widths</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function OppFilterBar({
  filters, defaults, stageOpts, serviceCategoryOpts, catFilter, onCatFilterChange, onChange,
}: {
  filters: OppFilters;
  defaults: OppFilters;
  stageOpts: PicklistValue[];
  serviceCategoryOpts: PicklistValue[];
  catFilter: string[];
  onCatFilterChange: (next: string[]) => void;
  onChange: (next: OppFilters) => void;
}) {
  const set = <K extends keyof OppFilters>(k: K, v: OppFilters[K]) => onChange({ ...filters, [k]: v });
  const isDefault = useMemo(
    () => JSON.stringify(filters) === JSON.stringify(defaults) && catFilter.length === 0,
    [filters, defaults, catFilter],
  );

  return (
    // Mobile (PWA): stack each control on its own line — wrapping looked cramped.
    // Desktop: inline, wrapping row.
    <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
      <SearchInput
        withIcon
        placeholder="Search"
        className="w-full md:w-56"
        value={filters.search}
        onValueChange={(v) => set('search', v)}
      />

      <MultiSelect
        label="Stage"
        options={stageOpts}
        selected={filters.stage}
        onChange={(v) => set('stage', v)}
      />

      <StringMultiSelect
        label="Service Category"
        options={serviceCategoryOpts}
        selected={catFilter}
        onChange={onCatFilterChange}
      />

      <Button
        variant="ghost"
        size="sm"
        onClick={() => { onChange(defaults); onCatFilterChange([]); }}
        disabled={isDefault}
        title={isDefault ? 'Filters are at defaults' : 'Reset filters'}
      >
        <RotateCcw className="mr-1 h-3 w-3" />Reset filters
      </Button>
    </div>
  );
}

// ---- Helpers ----

function fmtDate(d: string | null | undefined) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
}

function probKind(p: number): 'success' | 'warning' | 'destructive' {
  if (p >= 75) return 'success';
  if (p >= 40) return 'warning';
  return 'destructive';
}
